import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useRender, useT } from '../context';
import type { UiKey } from '../i18n';
import { loadRecaptcha, recaptchaToken } from '../recaptcha';
import { pushDataLayer } from '../tracking';
import type { Lang } from '../types';
import { RichHtml, TitleHighlight } from './common';

export interface AppointmentCalendarProps {
  title: string;
  highlight: string;
  introHtml: string;
  noticeHtml?: string;
  submitLabel: string;
  daysPerView: number;
  showChannelOptions: boolean;
}

interface Option {
  id: string;
  name: string;
}

interface CalendarConfig {
  timeZone: string;
  today: string;
  maxAdvanceDays: number;
  maxRangeDays: number;
  minYear: number;
  maxYear: number;
  topics: Option[];
  recaptchaSiteKey: string | null;
  /** Feedback channels enabled in the admin panel; a choice is only shown with two or more. */
  channels: Channel[];
}

type Channel = 'email' | 'sms' | 'whatsApp';

type SlotStatus = 'available' | 'limited' | 'full' | 'toosoon';
interface Slot {
  start: string;
  time: string;
  status: SlotStatus;
}
interface CalendarDay {
  date: string;
  status: 'open' | 'closed' | 'holiday' | 'full' | 'past';
  slots: Slot[];
}

type Field =
  | 'slot' | 'name' | 'phone' | 'email' | 'licensePlate' | 'message' | 'vehicleYear' | 'vehicleMake' | 'vehicleModel' | 'topics';
type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'error'; key: UiKey }
  | { kind: 'success'; reference: string; start: string };

const LOCALES: Record<Lang, string> = { de: 'de-DE', tr: 'tr-TR', en: 'en-GB', ru: 'ru-RU' };
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE = /^[0-9 +()/.-]{6,40}$/;

function addDays(date: string, days: number) {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function newKey() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID().replace(/-/g, '');
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

/**
 * Online appointment request. Rules, capacity and requests live in the database; this block only renders the
 * server-computed availability (never trusted: the server recomputes it on submit) and sends a request that an
 * admin approves. Selectable starts come from the masters' calendars, not from a fixed hourly capacity.
 */
export function AppointmentCalendar({ title, highlight, introHtml, noticeHtml, submitLabel, daysPerView, showChannelOptions }: AppointmentCalendarProps) {
  const t = useT();
  const { apiBase, lang, mode } = useRender();
  const locale = LOCALES[lang] ?? 'de-DE';
  const uid = useId().replace(/:/g, '');

  const [config, setConfig] = useState<CalendarConfig | null>(null);
  const [configError, setConfigError] = useState(false);
  const [from, setFrom] = useState<string | null>(null);
  const [days, setDays] = useState<CalendarDay[] | null>(null);
  const [daysError, setDaysError] = useState(false);
  const [reload, setReload] = useState(0);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [topics, setTopics] = useState<string[]>([]);

  const [year, setYear] = useState<string | null>(null);
  const [makes, setMakes] = useState<Option[]>([]);
  const [makeId, setMakeId] = useState<string | null>(null);
  const [models, setModels] = useState<Option[]>([]);
  const [modelId, setModelId] = useState<string | null>(null);
  const [notListed, setNotListed] = useState(false);

  const [values, setValues] = useState({ name: '', phone: '', email: '', licensePlate: '', message: '', vehicleNote: '', makeText: '', modelText: '' });
  const [channels, setChannels] = useState({ email: false, sms: false, whatsApp: false });
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Field, UiKey>>>({});
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const idempotencyKey = useRef<string | null>(null);

  const perView = Math.max(1, Math.min(daysPerView || 7, config?.maxRangeDays ?? 14));
  const topicsKey = [...topics].sort().join(',');
  const offered = config?.channels ?? [];

  useEffect(() => {
    let cancelled = false;
    setConfigError(false);
    fetch(`${apiBase}/api/public/appointments/config?lang=${lang}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((c: CalendarConfig) => {
        if (cancelled) return;
        setConfig(c);
        setFrom((f) => f ?? c.today);
      })
      .catch(() => !cancelled && setConfigError(true));
    return () => {
      cancelled = true;
    };
  }, [apiBase, lang, reload]);

  useEffect(() => {
    if (!config || !from) return;
    let cancelled = false;
    setDays(null);
    setDaysError(false);
    const qs = new URLSearchParams({ from, days: String(perView) });
    topics.forEach((id) => qs.append('topics', id));
    fetch(`${apiBase}/api/public/appointments/availability?${qs}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body: { calendar: CalendarDay[] }) => {
        if (cancelled) return;
        setDays(body.calendar);
        setActiveDate((current) =>
          current && body.calendar.some((d) => d.date === current)
            ? current
            : (body.calendar.find((d) => d.status === 'open') ?? body.calendar[0])?.date ?? null,
        );
        // A selection that is no longer offered is dropped (never silently moved to another time).
        setSlot((s) => {
          if (!s) return s;
          const still = body.calendar.flatMap((d) => d.slots).find((x) => x.start === s.start);
          return still && (still.status === 'available' || still.status === 'limited') ? s : null;
        });
      })
      .catch(() => !cancelled && setDaysError(true));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, config, from, perView, topicsKey, reload]);

  useEffect(() => {
    if (!config || notListed || !year) {
      setMakes([]);
      return;
    }
    let cancelled = false;
    fetch(`${apiBase}/api/public/appointments/vehicles/makes?year=${year}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : []))
      .then((list: Option[]) => {
        if (cancelled) return;
        setMakes(list);
        setMakeId((id) => (id && list.some((m) => m.id === id) ? id : null));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [apiBase, config, year, notListed]);

  useEffect(() => {
    if (!makeId || !year || notListed) {
      setModels([]);
      setModelId(null);
      return;
    }
    let cancelled = false;
    fetch(`${apiBase}/api/public/appointments/vehicles/models?makeId=${makeId}&year=${year}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : []))
      .then((list: Option[]) => {
        if (cancelled) return;
        setModels(list);
        setModelId((id) => (id && list.some((m) => m.id === id) ? id : null));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [apiBase, makeId, year, notListed]);

  const lastDate = config ? addDays(config.today, config.maxAdvanceDays) : null;
  const canPrev = !!config && !!from && from > config.today;
  const canNext = !!lastDate && !!from && addDays(from, perView) <= lastDate;
  const dayHeader = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' }), [locale]);
  const dayLong = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }), [locale]);
  const formatStart = (iso: string) =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'short', timeZone: config?.timeZone ?? 'Europe/Berlin' }).format(new Date(iso));

  const years = useMemo(() => {
    if (!config) return [];
    const list: Option[] = [];
    for (let y = config.maxYear; y >= config.minYear; y--) list.push({ id: String(y), name: String(y) });
    return list;
  }, [config]);

  const dayStatusKey = (d: CalendarDay): UiKey | null =>
    d.status === 'closed' ? 'apClosed' : d.status === 'holiday' ? 'apHoliday' : d.status === 'full' ? 'apDayFull' : d.status === 'past' ? 'apDayPast' : null;
  const slotStatusKey = (s: SlotStatus): UiKey => (s === 'available' ? 'apAvailable' : s === 'limited' ? 'apLimited' : 'apFull');

  const errorField: Partial<Record<keyof typeof values, Field>> = {
    name: 'name', phone: 'phone', email: 'email', licensePlate: 'licensePlate', message: 'message', makeText: 'vehicleMake', modelText: 'vehicleModel',
  };
  const set = (field: keyof typeof values, value: string) => {
    setValues((v) => ({ ...v, [field]: value }));
    // A corrected field no longer shows the error of the previous attempt.
    const f = errorField[field];
    if (f) setErrors((e) => (e[f] ? { ...e, [f]: undefined } : e));
  };

  const validate = () => {
    const e: Partial<Record<Field, UiKey>> = {};
    if (!slot) e.slot = 'apSelectSlot';
    if (values.name.trim().length < 2) e.name = 'fieldRequired';
    if (!PHONE.test(values.phone.trim())) e.phone = values.phone.trim() ? 'fieldPhone' : 'fieldRequired';
    if (values.email.trim() && !EMAIL.test(values.email.trim())) e.email = 'fieldEmail';
    if (offered.length > 1 && channels.email && !values.email.trim()) e.email = 'apEmailForNotify';
    if (values.licensePlate.trim().length < 2) e.licensePlate = 'fieldRequired';
    if (!year) e.vehicleYear = 'fieldRequired';
    if (notListed) {
      if (values.makeText.trim().length < 2) e.vehicleMake = 'fieldRequired';
      if (!values.modelText.trim()) e.vehicleModel = 'fieldRequired';
    } else {
      if (!makeId) e.vehicleMake = 'fieldRequired';
      if (!modelId) e.vehicleModel = 'fieldRequired';
    }
    if (values.message.trim().length < 5) e.message = 'fieldMessage';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const serverErrors = (errs: Record<string, unknown>) => {
    const e: Partial<Record<Field, UiKey>> = {};
    for (const [rawKey, rawValue] of Object.entries(errs)) {
      const key = (rawKey.charAt(0).toLowerCase() + rawKey.slice(1)) as Field;
      const value = Array.isArray(rawValue) ? String(rawValue[0]) : String(rawValue);
      e[key] =
        value === 'year_mismatch' ? 'apYearMismatch'
          : value === 'required_for_notification' ? 'apEmailForNotify'
          : key === 'licensePlate' && value === 'invalid' ? 'apPlateInvalid'
          : key === 'phone' ? 'fieldPhone'
          : key === 'email' ? 'fieldEmail'
          : 'fieldRequired';
    }
    setErrors(e);
  };

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (mode === 'editor' || status.kind === 'sending') return;
    if (!validate()) return setStatus({ kind: 'error', key: 'formInvalid' });
    setStatus({ kind: 'sending' });
    idempotencyKey.current ??= newKey();
    try {
      let captcha: string | null;
      try {
        captcha = await recaptchaToken(config?.recaptchaSiteKey, 'appointment_submit');
      } catch {
        return setStatus({ kind: 'error', key: 'captchaFailed' });
      }
      const res = await fetch(`${apiBase}/api/public/appointments`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey.current },
        body: JSON.stringify({
          name: values.name,
          phone: values.phone,
          email: values.email.trim() || null,
          licensePlate: values.licensePlate,
          vehicleYear: Number(year),
          vehicleMakeId: notListed ? null : makeId,
          vehicleModelId: notListed ? null : modelId,
          vehicleNotListed: notListed,
          vehicleMakeText: notListed ? values.makeText : null,
          vehicleModelText: notListed ? values.modelText : null,
          vehicleNote: values.vehicleNote.trim() || null,
          message: values.message,
          topicIds: topics,
          requestedStart: slot!.start,
          language: lang,
          // With a single enabled channel there is no choice; the server uses that channel.
          notifyByEmail: offered.includes('email') && (offered.length === 1 || channels.email),
          notifyBySms: offered.includes('sms') && (offered.length === 1 || channels.sms),
          notifyByWhatsApp: offered.includes('whatsApp') && (offered.length === 1 || channels.whatsApp),
          sourcePath: typeof window !== 'undefined' ? window.location.pathname : null,
          website,
          recaptchaToken: captcha,
        }),
      });
      const body = await res.json().catch(() => null);
      if (res.status === 202 || res.ok) {
        idempotencyKey.current = null;
        pushDataLayer({ event: 'appointment_request_submit', lead_type: 'appointment' });
        setStatus({ kind: 'success', reference: body.reference, start: body.requestedStart });
        return;
      }
      if (res.status === 409 && body?.title === 'slot_unavailable') {
        setSlot(null);
        setReload((n) => n + 1);
        setErrors({ slot: 'apSlotTaken' });
        return setStatus({ kind: 'error', key: 'apSlotTaken' });
      }
      if (res.status === 429) return setStatus({ kind: 'error', key: body?.title === 'too_many_requests' ? 'apTooMany' : 'formTooMany' });
      if (body?.title === 'captcha_failed') return setStatus({ kind: 'error', key: 'captchaFailed' });
      if (res.status === 503) return setStatus({ kind: 'error', key: 'formUnavailable' });
      if (res.status === 400 && body?.errors) {
        serverErrors(body.errors);
        return setStatus({ kind: 'error', key: 'formInvalid' });
      }
      setStatus({ kind: 'error', key: 'formError' });
    } catch {
      setStatus({ kind: 'error', key: 'formError' });
    }
  };

  const resetForm = () => {
    setSlot(null);
    setValues({ name: '', phone: '', email: '', licensePlate: '', message: '', vehicleNote: '', makeText: '', modelText: '' });
    setChannels({ email: false, sms: false, whatsApp: false });
    setTopics([]);
    setYear(null);
    setMakeId(null);
    setModelId(null);
    setNotListed(false);
    setErrors({});
    setStatus({ kind: 'idle' });
    setReload((n) => n + 1);
  };

  const errorText = (field: Field) =>
    errors[field] ? (
      <span id={`${uid}-${field}-err`} className="kfz-field-error">
        {t(errors[field]!)}
      </span>
    ) : null;

  const textInput = (field: 'name' | 'phone' | 'email' | 'licensePlate', label: UiKey, type: string, autoComplete: string, required: boolean) => (
    <div className="form-group style-two kfz-appt-field">
      <label htmlFor={`${uid}-${field}`}>
        {t(label)}
        {required ? <span className="kfz-appt-req" aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={`${uid}-${field}`}
        type={type}
        className={`form-control${errors[field] ? ' kfz-invalid' : ''}`}
        value={values[field]}
        autoComplete={autoComplete}
        required={required}
        aria-invalid={errors[field] ? true : undefined}
        aria-describedby={errors[field] ? `${uid}-${field}-err` : undefined}
        onChange={(e) => set(field, e.target.value)}
        disabled={mode === 'editor'}
      />
      {errorText(field)}
    </div>
  );

  if (status.kind === 'success') {
    return (
      <section className="contact_us kfz-appt">
        <div className="container">
          <div className="kfz-appt-success" role="status" aria-live="polite" tabIndex={-1} ref={(el) => el?.focus()}>
            <h2>{t('apSuccessTitle')}</h2>
            <p>{t('apSuccessText', { time: formatStart(status.start) })}</p>
            <p className="kfz-appt-reference">
              {t('apReference')}: <strong>{status.reference}</strong>
            </p>
            <button type="button" className="thm-btn" onClick={resetForm}>
              {t('apNewRequest')}
            </button>
          </div>
        </div>
      </section>
    );
  }


  return (
    <section className="contact_us kfz-appt">
      <div className="container">
        <div className="sec-title text-center">
          <h2>
            <TitleHighlight title={title} highlight={highlight} />
          </h2>
          <RichHtml html={introHtml} className="kfz-rich" />
        </div>

        {configError ? (
          <div className="kfz-appt-message" role="alert">
            <p>{t('apLoadError')}</p>
            <button type="button" className="thm-btn" onClick={() => setReload((n) => n + 1)}>
              {t('apRetry')}
            </button>
          </div>
        ) : !config ? (
          <p className="kfz-appt-message" role="status">{t('apLoading')}</p>
        ) : (
          <form
            className="default-form kfz-appt-form"
            noValidate
            onSubmit={onSubmit}
            onFocus={() => {
              // Google's script is only loaded once somebody starts filling in the form.
              if (config.recaptchaSiteKey && mode !== 'editor') void loadRecaptcha(config.recaptchaSiteKey).catch(() => undefined);
            }}
          >
            {/* Step 1: topics (they influence which masters count as capacity) and the time */}
            <fieldset className="kfz-appt-step">
              <legend>{t('apStepSlot')}</legend>

              {config.topics.length > 0 ? (
                <MultiCombobox
                  id={`${uid}-topics`}
                  label={t('apTopics')}
                  options={config.topics}
                  values={topics}
                  onChange={setTopics}
                  placeholder={t('apTopicsSearch')}
                  noOptions={t('apNoOptions')}
                  removeLabel={(name) => t('apRemove', { name })}
                  disabled={mode === 'editor'}
                />
              ) : null}

              <div className="kfz-appt-nav">
                <button type="button" className="kfz-appt-navbtn" disabled={!canPrev} onClick={() => from && config && setFrom(addDays(from, -perView) < config.today ? config.today : addDays(from, -perView))}>
                  <span aria-hidden="true">‹</span> {t('apPrevDays')}
                </button>
                <span className="kfz-appt-range" aria-live="polite">
                  {from ? `${dayHeader.format(new Date(`${from}T12:00:00Z`))} – ${dayHeader.format(new Date(`${addDays(from, perView - 1)}T12:00:00Z`))}` : ''}
                </span>
                <button type="button" className="kfz-appt-navbtn" disabled={!canNext} onClick={() => from && setFrom(addDays(from, perView))}>
                  {t('apNextDays')} <span aria-hidden="true">›</span>
                </button>
              </div>

              <Legend t={t} />

              {daysError ? (
                <div className="kfz-appt-message" role="alert">
                  <p>{t('apLoadError')}</p>
                  <button type="button" className="thm-btn" onClick={() => setReload((n) => n + 1)}>
                    {t('apRetry')}
                  </button>
                </div>
              ) : !days ? (
                <p className="kfz-appt-message" role="status">{t('apLoading')}</p>
              ) : (
                <>
                  {/* Day tabs are only shown on small screens; desktop shows all days side by side. */}
                  <div className="kfz-appt-daytabs" role="tablist" aria-label={t('apDays')}>
                    {days.map((d) => (
                      <button
                        key={d.date}
                        type="button"
                        role="tab"
                        aria-selected={activeDate === d.date}
                        className={`kfz-appt-daytab is-${d.status}${activeDate === d.date ? ' is-active' : ''}`}
                        onClick={() => setActiveDate(d.date)}
                      >
                        <span>{dayHeader.format(new Date(`${d.date}T12:00:00Z`))}</span>
                        {dayStatusKey(d) ? <small>{t(dayStatusKey(d)!)}</small> : null}
                      </button>
                    ))}
                  </div>
                  <div className="kfz-appt-grid" style={{ ['--kfz-days' as string]: String(days.length) }}>
                    {days.map((d) => {
                      const shown = d.slots.filter((s) => s.status !== 'toosoon');
                      const statusKey = dayStatusKey(d);
                      return (
                        <div key={d.date} className={`kfz-appt-day is-${d.status}${activeDate === d.date ? ' is-active' : ''}`} role="group" aria-label={dayLong.format(new Date(`${d.date}T12:00:00Z`))}>
                          <div className="kfz-appt-dayhead">
                            <span>{dayHeader.format(new Date(`${d.date}T12:00:00Z`))}</span>
                            {statusKey ? <small>{t(statusKey)}</small> : null}
                          </div>
                          <ul className="kfz-appt-slots">
                            {shown.map((s) => {
                              const selectable = s.status === 'available' || s.status === 'limited';
                              const selected = slot?.start === s.start;
                              return (
                                <li key={s.start}>
                                  <button
                                    type="button"
                                    className={`kfz-appt-slot is-${s.status}${selected ? ' is-selected' : ''}`}
                                    disabled={!selectable}
                                    aria-pressed={selectable ? selected : undefined}
                                    aria-label={`${dayLong.format(new Date(`${d.date}T12:00:00Z`))}, ${s.time} – ${t(slotStatusKey(s.status))}`}
                                    onClick={() => {
                                      setSlot(s);
                                      setErrors((e) => ({ ...e, slot: undefined }));
                                    }}
                                  >
                                    <span className="kfz-appt-slot-icon" aria-hidden="true">{s.status === 'available' ? '●' : s.status === 'limited' ? '◐' : '✕'}</span>
                                    <span className="kfz-appt-slot-time">{s.time}</span>
                                    {s.status !== 'available' ? <span className="kfz-appt-slot-note" aria-hidden="true">{t(slotStatusKey(s.status))}</span> : null}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                  {!days.some((d) => d.slots.some((s) => s.status === 'available' || s.status === 'limited')) ? (
                    <p className="kfz-appt-message">{t('apNoSlots')}</p>
                  ) : null}
                </>
              )}
              <p className="kfz-appt-muted">{t('apTimeZone', { tz: config.timeZone })}</p>
              <div className={`kfz-appt-selected${errors.slot ? ' has-error' : ''}`} aria-live="polite">
                {slot ? (
                  <>
                    <strong>{t('apSelected')}:</strong> {formatStart(slot.start)}{' '}
                    <button type="button" className="kfz-appt-link" onClick={() => setSlot(null)}>
                      {t('apChange')}
                    </button>
                  </>
                ) : (
                  <span id={`${uid}-slot-err`}>{t(errors.slot ?? 'apSelectSlot')}</span>
                )}
              </div>
            </fieldset>

            {/* Step 2: vehicle */}
            <fieldset className="kfz-appt-step">
              <legend>{t('apStepVehicle')}</legend>
              <div className="row clearfix">
                <div className="col-md-4 col-sm-4 col-xs-12">
                  <Combobox
                    id={`${uid}-year`}
                    label={t('apYear')}
                    required
                    options={years}
                    value={year}
                    onChange={(v) => {
                      setYear(v);
                      setErrors((e) => ({ ...e, vehicleYear: undefined, vehicleModel: undefined }));
                    }}
                    placeholder={t('apSearchPlaceholder')}
                    noOptions={t('apNoOptions')}
                    error={errors.vehicleYear ? t(errors.vehicleYear) : undefined}
                    disabled={mode === 'editor'}
                  />
                </div>
                {!notListed ? (
                  <>
                    <div className="col-md-4 col-sm-4 col-xs-12">
                      <Combobox
                        id={`${uid}-make`}
                        label={t('apMake')}
                        required
                        options={makes}
                        value={makeId}
                        onChange={(v) => {
                          setMakeId(v);
                          setModelId(null);
                          setErrors((e) => ({ ...e, vehicleMake: undefined }));
                        }}
                        placeholder={year ? t('apSearchPlaceholder') : t('apSelectYearFirst')}
                        noOptions={t('apNoOptions')}
                        error={errors.vehicleMake ? t(errors.vehicleMake) : undefined}
                        disabled={!year || mode === 'editor'}
                      />
                    </div>
                    <div className="col-md-4 col-sm-4 col-xs-12">
                      <Combobox
                        id={`${uid}-model`}
                        label={t('apModel')}
                        required
                        options={models}
                        value={modelId}
                        onChange={(v) => {
                          setModelId(v);
                          setErrors((e) => ({ ...e, vehicleModel: undefined }));
                        }}
                        placeholder={makeId ? t('apSearchPlaceholder') : t('apSelectMakeFirst')}
                        noOptions={t('apNoOptions')}
                        error={errors.vehicleModel ? t(errors.vehicleModel) : undefined}
                        disabled={!makeId || mode === 'editor'}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-4 col-sm-4 col-xs-12">
                      <FreeText id={`${uid}-maketext`} label={t('apMakeText')} value={values.makeText} onChange={(v) => set('makeText', v)} error={errors.vehicleMake ? t(errors.vehicleMake) : undefined} disabled={mode === 'editor'} />
                    </div>
                    <div className="col-md-4 col-sm-4 col-xs-12">
                      <FreeText id={`${uid}-modeltext`} label={t('apModelText')} value={values.modelText} onChange={(v) => set('modelText', v)} error={errors.vehicleModel ? t(errors.vehicleModel) : undefined} disabled={mode === 'editor'} />
                    </div>
                  </>
                )}
              </div>
              <label className="kfz-appt-check">
                <input
                  type="checkbox"
                  checked={notListed}
                  onChange={(e) => {
                    setNotListed(e.target.checked);
                    setErrors((x) => ({ ...x, vehicleMake: undefined, vehicleModel: undefined }));
                  }}
                  disabled={mode === 'editor'}
                />{' '}
                {t('apNotListed')}
              </label>
              {notListed ? (
                <FreeText id={`${uid}-vnote`} label={t('apVehicleNote')} value={values.vehicleNote} onChange={(v) => set('vehicleNote', v)} disabled={mode === 'editor'} optional />
              ) : null}
            </fieldset>

            {/* Step 3: contact details */}
            <fieldset className="kfz-appt-step">
              <legend>{t('apStepDetails')}</legend>
              <div className="row clearfix">
                <div className="col-md-6 col-sm-6 col-xs-12">{textInput('name', 'apName', 'text', 'name', true)}</div>
                <div className="col-md-6 col-sm-6 col-xs-12">{textInput('phone', 'apPhone', 'tel', 'tel', true)}</div>
                <div className="col-md-6 col-sm-6 col-xs-12">{textInput('email', 'apEmail', 'email', 'email', false)}</div>
                <div className="col-md-6 col-sm-6 col-xs-12">{textInput('licensePlate', 'apPlate', 'text', 'off', true)}</div>
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <div className="form-group style-two kfz-appt-field">
                    <label htmlFor={`${uid}-message`}>
                      {t('apMessage')}
                      <span className="kfz-appt-req" aria-hidden="true"> *</span>
                    </label>
                    <textarea
                      id={`${uid}-message`}
                      className={`form-control textarea${errors.message ? ' kfz-invalid' : ''}`}
                      value={values.message}
                      required
                      maxLength={3000}
                      aria-invalid={errors.message ? true : undefined}
                      aria-describedby={errors.message ? `${uid}-message-err` : undefined}
                      onChange={(e) => set('message', e.target.value)}
                      disabled={mode === 'editor'}
                    />
                    {errorText('message')}
                  </div>
                </div>
              </div>
              {showChannelOptions && offered.length > 1 ? (
                <fieldset className="kfz-appt-channels">
                  <legend>{t('apChannels')}</legend>
                  {offered.map((c) => (
                    <label key={c} className="kfz-appt-check">
                      <input
                        type="checkbox"
                        checked={channels[c]}
                        onChange={(e) => setChannels((x) => ({ ...x, [c]: e.target.checked }))}
                        disabled={mode === 'editor'}
                      />{' '}
                      {t(c === 'email' ? 'apChannelEmail' : c === 'sms' ? 'apChannelSms' : 'apChannelWhatsApp')}
                    </label>
                  ))}
                  <p className="kfz-appt-muted">{t('apChannelHint')}</p>
                </fieldset>
              ) : null}
            </fieldset>

            <div className="kfz-hp" aria-hidden="true">
              <label htmlFor={`${uid}-website`}>Website</label>
              <input id={`${uid}-website`} type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>

            {noticeHtml ? <RichHtml html={noticeHtml} className="kfz-rich kfz-appt-notice" /> : null}
            <div className="contact-section-btn text-center">
              <div className="form-group style-two">
                <button className="thm-btn thm-color" type="submit" disabled={status.kind === 'sending' || mode === 'editor'}>
                  {status.kind === 'sending' ? t('apSubmitting') : submitLabel}
                </button>
              </div>
              <div role="status" aria-live="polite" className="kfz-form-status">
                {status.kind === 'error' ? <p className="kfz-form-error">{t(status.key)}</p> : null}
                {mode === 'editor' ? <p className="kfz-appt-muted">{t('apEditorNote')}</p> : null}
              </div>
              {config.recaptchaSiteKey ? <p className="kfz-recaptcha-note">{t('recaptchaNotice')}</p> : null}
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

/**
 * Accessible searchable multi-select (WAI-ARIA combobox with a multi-selectable listbox). Selected options are shown
 * as removable chips; typing filters the list; Enter toggles the active option, Backspace removes the last chip.
 */
function MultiCombobox({ id, label, options, values, onChange, placeholder, noOptions, removeLabel, disabled }: {
  id: string; label: string; options: Option[]; values: string[]; onChange: (values: string[]) => void; placeholder: string;
  noOptions: string; removeLabel: (name: string) => string; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = options.filter((o) => values.includes(o.id));
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const toggle = (o: Option) => {
    onChange(values.includes(o.id) ? values.filter((v) => v !== o.id) : [...values, o.id]);
    setQuery('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[active]) {
        e.preventDefault();
        toggle(filtered[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Backspace' && !query && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div className="form-group style-two kfz-appt-field kfz-combo kfz-multi">
      <label htmlFor={id}>{label}</label>
      <div
        className={`kfz-multi-box${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !disabled) {
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
      >
        {selected.map((o) => (
          <span key={o.id} className="kfz-multi-chip">
            {o.name}
            <button
              type="button"
              aria-label={removeLabel(o.name)}
              disabled={disabled}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChange(values.filter((v) => v !== o.id))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          autoComplete="off"
          className="kfz-multi-input"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={open && filtered[active] ? `${id}-opt-${active}` : undefined}
          placeholder={values.length ? '' : placeholder}
          value={query}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false);
            setQuery('');
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
        <span className="kfz-multi-caret" aria-hidden="true">▾</span>
      </div>
      {open && !disabled ? (
        <ul id={`${id}-list`} role="listbox" aria-multiselectable="true" aria-label={label} className="kfz-combo-list" ref={listRef}>
          {filtered.length === 0 ? (
            <li className="kfz-combo-empty" role="presentation">{noOptions}</li>
          ) : (
            filtered.map((o, i) => {
              const checked = values.includes(o.id);
              return (
                <li
                  key={o.id}
                  id={`${id}-opt-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={checked}
                  className={`kfz-combo-option${i === active ? ' is-active' : ''}${checked ? ' is-selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggle(o);
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  <span className="kfz-multi-check" aria-hidden="true">{checked ? '✓' : ''}</span>
                  {o.name}
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}

function Legend({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <ul className="kfz-appt-legend" aria-label={t('apLegend')}>
      <li><span className="kfz-appt-slot-icon is-available" aria-hidden="true">●</span> {t('apAvailable')}</li>
      <li><span className="kfz-appt-slot-icon is-limited" aria-hidden="true">◐</span> {t('apLimited')}</li>
      <li><span className="kfz-appt-slot-icon is-full" aria-hidden="true">✕</span> {t('apFull')}</li>
      <li><span className="kfz-appt-slot-icon is-closed" aria-hidden="true">–</span> {t('apClosed')} / {t('apHoliday')}</li>
    </ul>
  );
}

function FreeText({ id, label, value, onChange, error, disabled, optional }: {
  id: string; label: string; value: string; onChange: (v: string) => void; error?: string; disabled?: boolean; optional?: boolean;
}) {
  return (
    <div className="form-group style-two kfz-appt-field">
      <label htmlFor={id}>
        {label}
        {!optional ? <span className="kfz-appt-req" aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        type="text"
        className={`form-control${error ? ' kfz-invalid' : ''}`}
        value={value}
        maxLength={optional ? 300 : 80}
        required={!optional}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
      {error ? <span id={`${id}-err`} className="kfz-field-error">{error}</span> : null}
    </div>
  );
}

/** Accessible searchable single select (WAI-ARIA combobox with listbox popup). */
function Combobox({ id, label, options, value, onChange, placeholder, noOptions, error, disabled, required }: {
  id: string; label: string; options: Option[]; value: string | null; onChange: (value: string | null) => void;
  placeholder?: string; noOptions: string; error?: string; disabled?: boolean; required?: boolean;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = options.find((o) => o.id === value) ?? null;
  const q = query.trim().toLowerCase();
  const filtered = (q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options).slice(0, 200);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const choose = (o: Option) => {
    onChange(o.id);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[active]) {
        e.preventDefault();
        choose(filtered[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div className="form-group style-two kfz-appt-field kfz-combo">
      <label htmlFor={id}>
        {label}
        {required ? <span className="kfz-appt-req" aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        className={`form-control${error ? ' kfz-invalid' : ''}`}
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[active] ? `${id}-opt-${active}` : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        aria-required={required || undefined}
        placeholder={placeholder}
        value={open ? query : selected?.name ?? ''}
        disabled={disabled}
        onFocus={() => {
          setOpen(true);
          setActive(Math.max(0, filtered.findIndex((o) => o.id === value)));
        }}
        onBlur={() => {
          setOpen(false);
          setQuery('');
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      {open && !disabled ? (
        <ul id={`${id}-list`} role="listbox" aria-label={label} className="kfz-combo-list" ref={listRef}>
          {filtered.length === 0 ? (
            <li className="kfz-combo-empty" role="presentation">{noOptions}</li>
          ) : (
            filtered.map((o, i) => (
              <li
                key={o.id}
                id={`${id}-opt-${i}`}
                data-index={i}
                role="option"
                aria-selected={o.id === value}
                className={`kfz-combo-option${i === active ? ' is-active' : ''}${o.id === value ? ' is-selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o);
                }}
                onMouseEnter={() => setActive(i)}
              >
                {o.name}
              </li>
            ))
          )}
        </ul>
      ) : null}
      {error ? <span id={`${id}-err`} className="kfz-field-error">{error}</span> : null}
    </div>
  );
}
